<script setup lang="ts">
import { ref, onMounted, watch } from "vue";
import { Field, ReportDesign, Table } from "../../../types/report";
import AddField from "./AddField.vue";
import IconComponent from "../../ui/common/IconComponent.vue";
import Buttons from "../../ui/form/Buttons.vue";
import TextInput from "../../ui/form/TextInput.vue";

const props = defineProps<{
  report: ReportDesign;
  data?: any;
  mode?: "create" | "edit" | "view" | "showQuickView" | "toggleStatus";
  error?: string | null;
}>();

const emit = defineEmits<{
  (e: "addField", data: Field): void;
}>();

const expandedTables = ref<string[]>([])
const showFieldProperties = ref(false)
const selectedField = ref<Field>()
const fieldToAdd = ref<Field>()

const toggleTable = (tableKey: string) => {
  fieldSearch.value = '';
  const index = expandedTables.value.indexOf(tableKey)
  if (index === -1) {
    expandedTables.value.push(tableKey)
  } else {
    expandedTables.value.splice(index, 1)
  }
}

const isTableExpanded = (tableKey: string) => {
  return expandedTables.value.includes(tableKey)
}

const selectField = (table: Table, field: any) => {
  fieldToAdd.value = {
    id: 'field_' + Math.random().toString(36).substring(2, 15),
    tableId: table.id,
    tableName: table.name,
    tableAlias: table.alias,
    name: field.field_name,
    alias: field.alias || field.name.replace(/[_-\s]+/g, '_').toLowerCase(),
    caption: field.caption || field.name,
    visible: true,
    aggregateFunction: undefined,
    decimals: 0,
    align: 'Left',
    type: field.field_type,
    width: 100,
    raw: false,
    rawQuery: "",
  };
  showFieldProperties.value = true
}

const addSqlField = () => {
  fieldToAdd.value = {
    id: 'field_' + Math.random().toString(36).substring(2, 15),
    tableId: '',
    tableName: '',
    tableAlias: '',
    name: '',
    alias: '',
    caption: '',
    visible: true,
    aggregateFunction: undefined,
    decimals: 0,
    align: 'Left',
    type: 'string',
    width: 100,
    raw: true,
    rawQuery: "",
  };
  showFieldProperties.value = true
}

const addFieldToReport = (field: Field) => {
  emit("addField", field);
  showFieldProperties.value = false;
}
/*const addFieldToReport = () => {
  if (props.report && selectedField.value) {
    props.report.fields.push({
      tableField: selectedField.value.tableField,
      alias: selectedField.value.alias,
      caption: selectedField.value.caption,
      visible: selectedField.value.visible,
      align: selectedField.value.align
    })
    showFieldProperties.value = false
    selectedField.value = null
  }
}

const removeField = (index: number) => {
  if (props.report.value) {
    props.report.value.fields.splice(index, 1)
  }
}*/

const moveFieldUp = (index: number) => {
  if (index > 0) {
    const field = props.report.fields[index];
    props.report.fields.splice(index, 1);
    props.report.fields.splice(index - 1, 0, field);
    props.report.fields.forEach((f, i) => {
      f.sort_order = i;
    });
  }
};

const moveFieldDown = (index: number) => {
  if (index < props.report.fields.length - 1) {
    const field = props.report.fields[index];
    props.report.fields.splice(index, 1);
    props.report.fields.splice(index + 1, 0, field);
    props.report.fields.forEach((f, i) => {
      f.sort_order = i;
    });
  }
};

const removeField = (index: number) => {
  if (props.report.fields && index >= 0 && index < props.report.fields.length) {
    props.report.fields.splice(index, 1);
    props.report.fields.forEach((f, i) => {
      f.sort_order = i;
    });
  }
}

const viewField = (field: Field) => {
  selectedField.value = field
  showFieldProperties.value = true
}

const dragStart = (index: number) => {
  draggingIndex.value = index;
};
const draggingIndex = ref<number | null>(null);
const dragOver = (index: number) => {
  if (draggingIndex.value !== null && draggingIndex.value !== index) {
    const field = props.report.fields[draggingIndex.value];
    props.report.fields.splice(draggingIndex.value, 1);
    props.report.fields.splice(index, 0, field);
    draggingIndex.value = index;
    props.report.fields.forEach((f, i) => {
      f.sort_order = i;
    });
  }
};
const drop = (_index: number) => {
  draggingIndex.value = null;
};

onMounted(() => {
  console.log("StepField component mounted");
  console.log(props.report);
});

const fieldSearch = ref('')
const searchFields = () => {
  console.log('search fields', fieldSearch.value)
}

watch(() => props.report, () => {
  console.log(props.report.rawQuery)
  if (props.report.rawQuery && props.report.rawReport) {
    console.log(props.report.rawQuery)
  }
})



watch(() => props.report.rawQuery, () => {
  if (props.report.rawQuery && props.report.rawReport) {
    console.log(props.report.rawQuery)
  }
})


</script>

<template>
  <div class="fields-section section">
    <div class="section-header" style="display: flex; align-items: center; justify-content: space-between;">
      <div id="step-name" style="display: flex; align-items: center;">
      <div class="step-indicator">Step 3</div>
      <h2 style="margin-left: 0.75rem;">Fields</h2>
      </div>
      <div id="add-raw-field">
      <Buttons
        caption="Add SQL Query Field"
        icon="Plus" 
        @click="addSqlField"
      />
      </div>      
    </div>
    <p v-if="error" class="mt-1 text-sm text-red-600 dark:text-red-400">
          {{ error }}
        </p>

    <div class="fields-container">
      <div class="available-fields">
        <h3>Available Fields</h3>
        <div class="field-tree">
          <div v-for="table in report.tables" :key="table.id" class="table-node">
            <div class="table-header">
              <i
                class="pi"
                :class="
                  isTableExpanded(table.id)
                    ? 'pi-chevron-down'
                    : 'pi-chevron-right'
                "
              ></i>
              <IconComponent name="Table" @click="toggleTable(table.id)"/>
              <span @click="toggleTable(table.id)">{{ table.displayName }}</span>              
            </div>
            <div v-if="isTableExpanded(table.id)" class="field-list">
              <TextInput label="" v-model="fieldSearch" @update:modelValue="searchFields"/>
              <div
                v-for="field in (fieldSearch ? table.fields.filter((e) => (e.name || '').toLowerCase().includes(fieldSearch)) : table.fields)"
                :key="field.name"
                class="field-item"
                @click="selectField(table, field)"
              >
                <IconComponent name="BetweenHorizontalStart" />
                <span>{{ field.name }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="selected-fields">
        <h3>Selected Fields</h3>
        <div class="fields-list">
          <div v-if="report.fields.length === 0" class="empty-state">
            <p>
              No fields selected. Click on fields from the available tables to
              add them to your report.
            </p>
          </div>
            <table class="divide-y divide-gray-200 dark:divide-gray-700 w-full" style="width: 100%; border-collapse: collapse;" v-else>
              <thead>                  
                <tr class="px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  <th style="text-align: left; padding: 0.5rem;">Caption</th>
                  <th style="text-align: left; padding: 0.5rem;">Type</th>
                  <th style="text-align: right; padding: 0.5rem;">Order</th>
                  <th style="text-align: center; padding: 0.5rem;" class="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider sticky right-0 bg-gray-50 dark:bg-gray-800 shadow-l">...</th>
                </tr>
              </thead>
              <tbody class="bg-white text-sm py-4 dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                <tr
                  v-for="(field, index) in report.fields"
                  :key="field.id"
                  style="border-bottom: 1px solid #e9ecef;"
                  draggable="true"
                  @dragstart="dragStart(index)"
                  @dragover.prevent="dragOver(index)"
                  @drop="drop(index)"
                  :class="{ 'dragging': draggingIndex === index }"
                  class="hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                    <td style="padding: 0.5rem; display: flex; align-items: center;">
                    <IconComponent name="GripVertical" style="margin-right: 0.5rem; cursor: grab;" />
                    {{ field.caption }}
                    </td>
                  <td style="padding: 0.5rem; color: #888;">{{ field.type }}</td>
                  <td style="padding: 0.5rem; text-align: right;">{{ field.sort_order }}</td>
                  <td style="padding: 0.5rem; text-align: center;">
                    <button
                      :disabled="index == 0"
                      @click="moveFieldUp(index)"
                      class="mr-2"
                      title="Move Up"
                      :style="{ color: index == 0 ? 'white' : '' }"
                    >
                      <IconComponent name="CircleArrowUp" />
                    </button>
                    <button
                      :disabled="index == report.fields.length - 1"
                      @click="moveFieldDown(index)"
                      class="mr-2"
                      title="Move Down"
                      :style="{ color: index == report.fields.length - 1 ? 'white' : '' }"
                    >
                      <IconComponent name="CircleArrowDown" />
                    </button>
                    <button
                      id="table-action-remove-table"
                      @click="viewField(field)"
                      class="text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-600"
                      title="Delete Table"
                    >
                      <IconComponent name="Eye"/>
                    </button>
                    <button
                      id="table-action-remove-table"
                      @click="removeField(index)"
                      class="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-600"
                      title="Delete Table"
                    >
                      <IconComponent name="Trash2"/>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
        </div>
      </div>
    </div>
  </div>
  <AddField
    v-if="showFieldProperties"
    :report="report"
    :field="fieldToAdd"
    :mode="selectedField ? 'edit' : 'create'"
    :title="selectedField ? 'Edit' + selectedField.name + '('+ selectedField.alias +')' : 'Add New Field'"
    :data="selectedField"
    @close="showFieldProperties = false"
    @addField="addFieldToReport" />
</template>
<style scoped>


.fields-container {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
}

.available-fields, .selected-fields {
  background-color: #f8f9fa;
  border-radius: 4px;
  padding: 1rem;
}

.available-fields h3, .selected-fields h3 {
  font-size: 1rem;
  margin: 0 0 1rem 0;
  color: #495057;
}

.field-tree {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.table-node {
  border: 1px solid #e9ecef;
  border-radius: 4px;
  overflow: hidden;
}

.table-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem;
  background-color: white;
  cursor: pointer;
  user-select: none;
}

.table-header:hover {
  background-color: #f8f9fa;
}

.field-list {
  padding: 0.5rem;
  margin-left: 1.2rem;
  background-color: white;
}

.field-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem;
  cursor: pointer;
  border-radius: 4px;
}

.field-item:hover {
  background-color: #f8f9fa;
}

.field-item i {
  color: #6c757d;
  font-size: 0.875rem;
}

.empty-state {
  text-align: center;
  padding: 2rem;
  background-color: #f8f9fa;
  border-radius: 4px;
  color: #6c757d;
}
</style>